import { useEffect, useRef } from "react";
import Button from "../../../components/base/Button";
import Rendering1 from "@/assets/images/3D/1.png";
import Rendering2 from "@/assets/images/3D/2.jpg";
import Rendering3 from "@/assets/images/3D/3.jpg";
import Rendering4 from "@/assets/images/3D/4.jpg";
import Rendering5 from "@/assets/images/3D/5.jpg";
import Rendering6 from "@/assets/images/3D/6.png";
import Rendering7 from "@/assets/images/3D/7.png";
import Rendering8 from "@/assets/images/3D/8.png";
import Rendering9 from "@/assets/images/3D/9.png";
import FlooPlan1 from "@/assets/images/floorplan/1.png";
import FlooPlan2 from "@/assets/images/floorplan/2.png";
import FlooPlan3 from "@/assets/images/floorplan/3.png";
import FlooPlan4 from "@/assets/images/floorplan/4.png";
import FlooPlan5 from "@/assets/images/floorplan/5.png";
import FlooPlan6 from "@/assets/images/floorplan/6.png";
import Lighting1 from "@/assets/images/lighting/1.png";
import Lighting2 from "@/assets/images/lighting/2.png";
import Lighting3 from "@/assets/images/lighting/3.png";

export default function HeroSectionV() {
  const column1Ref = useRef<HTMLDivElement>(null);
  const column2Ref = useRef<HTMLDivElement>(null);
  const column3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const animateColumn = (element: HTMLElement, direction: 'up' | 'down') => {
      if (!element) return;

      const speed = 0.5;
      let position = 0;
      const totalHeight = element.scrollHeight / 2; // height of one set

      const animate = () => {
        if (direction === 'up') {
          position -= speed;
          if (Math.abs(position) >= totalHeight) position = 0;
          element.style.transform = `translateY(${position}px)`;
        } else {
          // Fixed: Start from negative position for downward scroll
          position += speed;
          if (position >= 0) position = -totalHeight;
          element.style.transform = `translateY(${position}px)`;
        }
        requestAnimationFrame(animate);
      };

      // Initialize position for downward scrolling
      if (direction === 'down') {
        position = -totalHeight;
        element.style.transform = `translateY(${position}px)`;
      }

      animate();
    };

    if (column1Ref.current) animateColumn(column1Ref.current, 'up');
    if (column2Ref.current) animateColumn(column2Ref.current, 'down');
    if (column3Ref.current) animateColumn(column3Ref.current, 'up');
  }, []);

  const portfolioImages = {
    column1: [Rendering1, FlooPlan1, Lighting1, Rendering2, FlooPlan2, Rendering3],
    column2: [Rendering4, FlooPlan3, Lighting2, Rendering5, FlooPlan4, Rendering6],
    column3: [Rendering7, FlooPlan5, Lighting3, Rendering8, FlooPlan6, Rendering9],
  };

  const renderColumn = (images: string[], ref: any, direction: 'up' | 'down') => {
    // Duplicate content for seamless scroll
    const duplicatedImages = [...images, ...images];
    return (
      <div className="flex-1 overflow-hidden">
        <div ref={ref} className="flex flex-col gap-4" style={{ willChange: 'transform' }}>
          {duplicatedImages.map((image, index) => (
            <div key={index} className="relative group cursor-pointer">
              <img
                src={image}
                alt={`Portfolio ${index + 1}`}
                className="w-full h-48 lg:h-56 object-cover rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[#e18033]/0 group-hover:bg-[#e18033]/20 transition-all duration-300 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-white overflow-hidden">
      {/* Background Image with Backdrop */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8 lg:pr-8">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                Precision <span className="text-[#e18033]">Drafting</span> & <span className="text-[#e18033]">Takeoff</span> Services
              </h1>
              <p className="text-xl lg:text-2xl text-white/80 max-w-xl leading-relaxed">
                we support your projects with reliable, technical documentation and estimating services.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/portfolio"><Button variant="primary" size="lg">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-ruler-line"></i>
                </div>
                Drafting Services
              </Button></a>
              <a href="/services"><Button variant="outline" size="lg" className="border-white text-white hover:bg-[#e18033] hover:text-white">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calculator-line"></i>
                </div>
                Takeoff Services
              </Button></a>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-8 pt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#e18033]">50+</div>
                <div className="text-sm text-[#889d8f]">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#e18033]">21+</div>
                <div className="text-sm text-[#889d8f]">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#e18033]">98%</div>
                <div className="text-sm text-[#889d8f]">Client Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Side - Scrolling Image Gallery */}
          <div className="relative h-[600px] lg:h-[700px] overflow-hidden">
            <div className="flex gap-4 h-full">
              {renderColumn(portfolioImages.column1, column1Ref, 'up')}
              {renderColumn(portfolioImages.column2, column2Ref, 'down')}
              <div className="flex-1 overflow-hidden hidden sm:block">
                {renderColumn(portfolioImages.column3, column3Ref, 'up')}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}